import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircleCheck, CircleMinus, LoaderCircle, Plus, TriangleAlert, WandSparkles, X } from "lucide-react";
import { SwalWithTheme } from "../utils/sweetalert";
import FiltersSheet from "../components/FiltersSheet";
import usePageFilters from "../hooks/usePageFilters";
import { getUsuarioLogado } from "../services/LoginService";
import { buscarEnderecoPorCep } from "../services/cepService";
import { criarTarefa } from "../services/tarefaService";
import { listarTiposDeTarefa } from "../services/tipoDeTarefaService";
import { listarMotivosTarefa } from "../services/motivoTarefaService";
import { listarUsuariosPaginado } from "../services/usuarioService";
import { listarParceirosFiltros, listarParceirosPaginado, listarTodosParceirosFiltrados, salvarParceiro } from "../services/parceiroService";
import { buscarProducaoParceiros } from "../services/dashboardService";
import { formatCpfCnpj, getCpfCnpjLabel } from "../utils/inputMasks";
import { normalizeRankingParceiros } from "../utils/dashboardSupervisor";
import { PERIOD_PRESETS, resolvePeriodPreset } from "../utils/periodPresets";
import { allowsMultiplePartners } from "../utils/taskRules";
import { buildTaskTitleFromNotes } from "../utils/taskText";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DataTable,
  DatePickerField,
  FormField,
  getAvatarInitials,
  Modal,
  MultiSelectField,
  SearchBar,
  Skeleton,
  StatusBadge,
} from "../components/ui";

const PAGE_SIZE = 20;
const TASK_SCHEDULE_WINDOW_DAYS = 90;
const TASK_SCHEDULE_PAST_WINDOW_DAYS = 14;
const FILTER_STORAGE_KEY = "lista-parceiros:filtros:v1";
const CURRENCY_FORMATTER = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const calcProgress = (checks = []) => {
  const total = checks.length || 1;
  const done = checks.filter(Boolean).length;
  return Math.round((done / total) * 100);
};

function toDateTimeLocal(value) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function normalizeDateTime(value) {
  if (!value) return null;
  const normalized = value.length === 16 ? `${value}:00` : value;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(normalized);
  if (match) {
    const [, year, month, day, hour, minute, second] = match;
    const localDate = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second || 0),
      0
    );
    if (!Number.isNaN(localDate.getTime())) {
      return localDate.toISOString();
    }
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

function buildTaskScheduleBounds(now = new Date()) {
  const minDate = new Date(now);
  minDate.setDate(minDate.getDate() - TASK_SCHEDULE_PAST_WINDOW_DAYS);
  minDate.setHours(0, 0, 0, 0);
  const maxDate = new Date(now);
  maxDate.setDate(maxDate.getDate() + TASK_SCHEDULE_WINDOW_DAYS);
  return {
    min: toDateTimeLocal(minDate),
    max: toDateTimeLocal(maxDate),
  };
}

function isTaskScheduleOutsideWindow(value, bounds) {
  if (!value || !bounds?.min || !bounds?.max) return false;
  const selectedDate = new Date(normalizeDateTime(value));
  const minDate = new Date(normalizeDateTime(bounds.min));
  const maxDate = new Date(normalizeDateTime(bounds.max));

  if ([selectedDate, minDate, maxDate].some((date) => Number.isNaN(date.getTime()))) {
    return false;
  }

  return selectedDate < minDate || selectedDate > maxDate;
}

function loadFiltersFromSession() {
  const defaultProductionPeriod = resolvePeriodPreset("LAST_31_DAYS");
  const fallback = {
    page: 0,
    pageSize: PAGE_SIZE,
    searchTerm: "",
    filterUFs: [],
    filterCidades: [],
    filterResponsavelIds: [],
    filterStatuses: [],
    productionPeriodPreset: "LAST_31_DAYS",
    productionStartDate: defaultProductionPeriod.dataInicio,
    productionEndDate: defaultProductionPeriod.dataFim,
  };

  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.sessionStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    const productionPeriodPreset = String(parsed?.productionPeriodPreset || fallback.productionPeriodPreset);
    const productionPeriod = productionPeriodPreset === "CUSTOM"
      ? {
          dataInicio: String(parsed?.productionStartDate || fallback.productionStartDate),
          dataFim: String(parsed?.productionEndDate || fallback.productionEndDate),
        }
      : resolvePeriodPreset(productionPeriodPreset);
    return {
      ...fallback,
      page: Number.isFinite(parsed?.page) ? Math.max(0, Number(parsed.page)) : fallback.page,
      pageSize: Number.isFinite(parsed?.pageSize) ? Number(parsed.pageSize) : fallback.pageSize,
      searchTerm: String(parsed?.searchTerm || ""),
      filterUFs: Array.isArray(parsed?.filterUFs) ? parsed.filterUFs.map(String) : [],
      filterCidades: Array.isArray(parsed?.filterCidades) ? parsed.filterCidades.map(String) : [],
      filterResponsavelIds: Array.isArray(parsed?.filterResponsavelIds) ? parsed.filterResponsavelIds.map(String) : [],
      filterStatuses: Array.isArray(parsed?.filterStatuses) ? parsed.filterStatuses.map(String) : [],
      productionPeriodPreset,
      productionStartDate: productionPeriod.dataInicio,
      productionEndDate: productionPeriod.dataFim,
    };
  } catch {
    return fallback;
  }
}

export default function ListaParceiros() {
  const navigate = useNavigate();
  const initialFilters = useMemo(() => loadFiltersFromSession(), []);
  const [usuarioLogado, setUsuarioLogado] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosLoading, setUsuariosLoading] = useState(true);

  const [partnerPage, setPartnerPage] = useState({
    content: [],
    page: 0,
    totalPages: 0,
    totalElements: 0,
    size: PAGE_SIZE,
  });
  const [page, setPage] = useState(initialFilters.page);
  const [pageSize, setPageSize] = useState(initialFilters.pageSize || PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState(initialFilters.searchTerm);
  const [filterUFs, setFilterUFs] = useState(initialFilters.filterUFs);
  const [filterCidades, setFilterCidades] = useState(initialFilters.filterCidades);
  const [filterResponsavelIds, setFilterResponsavelIds] = useState(initialFilters.filterResponsavelIds);
  const [filterStatuses, setFilterStatuses] = useState(initialFilters.filterStatuses);
  const [productionPeriodPreset, setProductionPeriodPreset] = useState(initialFilters.productionPeriodPreset);
  const [productionStartDate, setProductionStartDate] = useState(initialFilters.productionStartDate);
  const [productionEndDate, setProductionEndDate] = useState(initialFilters.productionEndDate);
  const [productionByJoinId, setProductionByJoinId] = useState(() => new Map());
  const [productionLookupStatus, setProductionLookupStatus] = useState("LOADING");
  const [filterOptions, setFilterOptions] = useState({ ufs: [], cidades: [], statuses: [] });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    nome: "",
    documento: "",
    cep: "",
    endereco: "",
    bairro: "",
    cidade: "",
    uf: "",
    nu_endereco: "",
    usuarioId: "",
    status: "ativo",
  });

  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskForm, setTaskForm] = useState({
    tipoId: "",
    dataHora: "",
    motivoIds: [],
    observacoes: "",
  });
  const [tiposTarefa, setTiposTarefa] = useState([]);
  const [motivosTarefa, setMotivosTarefa] = useState([]);
  const [savingTaskModal, setSavingTaskModal] = useState(false);
  const [selectedPartnerIds, setSelectedPartnerIds] = useState([]);
  const [selectedPartnersMap, setSelectedPartnersMap] = useState({});
  const [allMatchingSelected, setAllMatchingSelected] = useState(false);
  const [selectAllMatchingLoading, setSelectAllMatchingLoading] = useState(false);
  const [responsavelQuery, setResponsavelQuery] = useState("");
  const [responsavelListOpen, setResponsavelListOpen] = useState(false);
  const [responsavelManualInput, setResponsavelManualInput] = useState(false);
  const responsavelInputRef = useRef(null);
  const responsavelDropdownRef = useRef(null);

  const taskScheduleBounds = useMemo(() => buildTaskScheduleBounds(), [showTaskModal]);

  useEffect(() => {
    (async () => {
      try {
        setUsuariosLoading(true);
        const user = await getUsuarioLogado();
        setUsuarioLogado(user);
        if (user?.cargo?.toLowerCase() === "admin") {
          const usersPage = await listarUsuariosPaginado({ page: 0, size: 200, ativo: true });
          setUsuarios(usersPage.content.filter((u) => u.cargo === "user"));
        }
      } catch {
        setUsuarioLogado(null);
      } finally {
        setUsuariosLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    carregarParceiros();
  }, [page, pageSize, searchTerm, filterUFs, filterCidades, filterResponsavelIds, filterStatuses]);

  useEffect(() => {
    let cancelled = false;

    const carregarProducao = async () => {
      if (!productionStartDate || !productionEndDate || productionEndDate < productionStartDate) {
        setProductionByJoinId(new Map());
        setProductionLookupStatus("UNAVAILABLE");
        return;
      }

      setProductionLookupStatus("LOADING");
      try {
        const response = await buscarProducaoParceiros({
          responsavelIds: filterResponsavelIds.map(Number),
          dataInicio: productionStartDate,
          dataFim: productionEndDate,
          tipoPeriodo: "APROVACAO",
        });
        if (cancelled) return;
        if (response?.completo === false) {
          setProductionByJoinId(new Map());
          setProductionLookupStatus("UNAVAILABLE");
          return;
        }

        const items = normalizeRankingParceiros({ rankingParceiros: response?.parceiros || [] });
        setProductionByJoinId(new Map(
          items
            .filter((item) => Number(item?.idJoin) > 0)
            .map((item) => [Number(item.idJoin), Number(item.producaoBrutaTotal || 0)])
        ));
        setProductionLookupStatus("READY");
      } catch {
        if (cancelled) return;
        setProductionByJoinId(new Map());
        setProductionLookupStatus("UNAVAILABLE");
      }
    };

    carregarProducao();
    return () => {
      cancelled = true;
    };
  }, [filterResponsavelIds, productionStartDate, productionEndDate]);

  useEffect(() => {
    carregarOpcoesFiltro();
  }, [searchTerm, filterUFs, filterCidades, filterResponsavelIds, filterStatuses]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(
      FILTER_STORAGE_KEY,
      JSON.stringify({
        page,
        pageSize,
        searchTerm,
        filterUFs,
        filterCidades,
        filterResponsavelIds,
        filterStatuses,
        productionPeriodPreset,
        productionStartDate,
        productionEndDate,
      })
    );
  }, [page, pageSize, searchTerm, filterUFs, filterCidades, filterResponsavelIds, filterStatuses, productionPeriodPreset, productionStartDate, productionEndDate]);


  useEffect(() => {
    const handleResponsavelClose = (event) => {
      if (
        responsavelDropdownRef.current &&
        responsavelInputRef.current &&
        !responsavelDropdownRef.current.contains(event.target) &&
        !responsavelInputRef.current.contains(event.target)
      ) {
        setResponsavelListOpen(false);
      }
    };
    document.addEventListener("mousedown", handleResponsavelClose);
    return () => document.removeEventListener("mousedown", handleResponsavelClose);
  }, []);

  const carregarParceiros = async () => {
    setLoading(true);
    try {
      const data = await listarParceirosPaginado({
        page,
        size: pageSize,
        q: searchTerm.trim(),
        ufs: filterUFs,
        cidades: filterCidades,
        responsavelIds: filterResponsavelIds,
        statuses: filterStatuses,
      });
      setPartnerPage(data);
    } catch {
      SwalWithTheme.error("Erro", "Erro ao listar parceiros");
    } finally {
      setLoading(false);
    }
  };

  const carregarOpcoesFiltro = async () => {
    try {
      const data = await listarParceirosFiltros({
        q: searchTerm.trim(),
        ufs: filterUFs,
        cidades: filterCidades,
        responsavelIds: filterResponsavelIds,
        statuses: filterStatuses,
      });
      setFilterOptions(data);
    } catch {
      setFilterOptions({ ufs: [], cidades: [], statuses: [] });
    }
  };

  const parceirosFiltrados = useMemo(() => (partnerPage.content || []).map((partner) => {
    const idJoin = Number(partner?.idJoin);
    const hasJoinLink = Number.isFinite(idJoin) && idJoin > 0;

    if (!hasJoinLink) {
      return { ...partner, producaoPeriodo: null, producaoStatus: "UNVERIFIED" };
    }
    if (productionLookupStatus === "LOADING") {
      return { ...partner, producaoPeriodo: null, producaoStatus: "LOADING" };
    }
    if (productionLookupStatus !== "READY") {
      return { ...partner, producaoPeriodo: null, producaoStatus: "UNVERIFIED" };
    }

    const producaoPeriodo = Number(productionByJoinId.get(idJoin) || 0);
    return {
      ...partner,
      producaoPeriodo,
      producaoStatus: producaoPeriodo > 0 ? "WITH" : "WITHOUT",
    };
  }), [partnerPage.content, productionByJoinId, productionLookupStatus]);

  useEffect(() => {
    setSelectedPartnerIds([]);
    setSelectedPartnersMap({});
    setAllMatchingSelected(false);
  }, [searchTerm, filterUFs, filterCidades, filterResponsavelIds, filterStatuses, productionStartDate, productionEndDate, pageSize]);

  const ufOptions = useMemo(() => {
    return Array.isArray(filterOptions.ufs) ? filterOptions.ufs : [];
  }, [filterOptions.ufs]);

  const cidadeOptions = useMemo(() => {
    return Array.isArray(filterOptions.cidades) ? filterOptions.cidades : [];
  }, [filterOptions.cidades]);

  const statusOptions = useMemo(() => {
    const base = ["ativo", "lead", "inativo"];
    const dynamic = (filterOptions.statuses || [])
      .map((status) => String(status || "").toLowerCase().trim())
      .filter(Boolean);
    return Array.from(new Set([...base, ...dynamic])).map((status) => ({
      value: status,
      label: status.charAt(0).toUpperCase() + status.slice(1),
    }));
  }, [filterOptions.statuses]);

  const responsavelOptions = useMemo(() => {
    const source =
      usuarios.length > 0
        ? usuarios
        : parceirosFiltrados.map((p) => ({
          id: p.usuarioId ?? p.usuario?.id,
          nome: p.usuarioNome || p.usuario?.nome || "Responsavel",
          email: p.usuario?.email,
          fotoPerfilUrl: p.usuarioFotoPerfilUrl ?? p.usuario?.fotoPerfilUrl ?? "",
        }));
    const unique = new Map();
    source.forEach((user) => {
      if (!user?.id) return;
      const lookupKey = String(user.id);
      const previous = unique.get(lookupKey);
      unique.set(lookupKey, {
        ...previous,
        ...user,
        nome: user.nome || previous?.nome || `ID ${user.id}`,
        email: user.email || previous?.email || "",
        fotoPerfilUrl: user.fotoPerfilUrl || previous?.fotoPerfilUrl || "",
      });
    });
    return Array.from(unique.values());
  }, [usuarios, parceirosFiltrados]);

  const responsavelLookup = useMemo(() => {
    const map = new Map();
    responsavelOptions.forEach((user) => {
      map.set(String(user.id), {
        id: user.id,
        nome: user.nome || `ID ${user.id}`,
        email: user.email || "",
        fotoPerfilUrl: user.fotoPerfilUrl || "",
      });
    });
    return map;
  }, [responsavelOptions]);

  const responsavelMap = useMemo(() => {
    const map = new Map();
    responsavelLookup.forEach((user, key) => {
      map.set(key, user.nome || `ID ${user.id}`);
    });
    return map;
  }, [responsavelLookup]);

  const selectedResponsavel = useMemo(() => {
    return responsavelOptions.find((user) => String(user.id) === String(form.usuarioId));
  }, [form.usuarioId, responsavelOptions]);

  const responsavelSuggestions = useMemo(() => {
    const term = responsavelQuery.trim().toLowerCase();
    if (!term) {
      return responsavelOptions.slice(0, 8);
    }
    return responsavelOptions.filter((user) =>
      user?.nome?.toLowerCase().includes(term)
    );
  }, [responsavelOptions, responsavelQuery]);

  useEffect(() => {
    if (responsavelManualInput) return;
    if (selectedResponsavel) {
      setResponsavelQuery(selectedResponsavel.nome);
    } else {
      setResponsavelQuery("");
    }
  }, [selectedResponsavel, responsavelManualInput]);

  const activeFilterCount =
    filterUFs.length + filterCidades.length + filterResponsavelIds.length + filterStatuses.length +
    (productionPeriodPreset !== "LAST_31_DAYS" ? 1 : 0);

  // Filtros abrem pelo funil da barra superior, no painel lateral padrão (FiltersSheet).
  usePageFilters({ count: activeFilterCount, onOpen: () => setFiltersOpen(true) });

  const handleCepBlur = async () => {
    if (!form.cep) return;
    try {
      const endereco = await buscarEnderecoPorCep(form.cep.replace("-", ""));
      if (endereco?.cepGeral) {
        SwalWithTheme.warning(
          "CEP",
          "Este CEP é geral da cidade e não possui logradouro. Informe rua e bairro manualmente."
        );
      }
      setForm((prev) => ({
        ...prev,
        endereco: endereco.logradouro || prev.endereco,
        bairro: endereco.bairro || prev.bairro,
        cidade: endereco.localidade || prev.cidade,
        uf: endereco.uf || prev.uf,
      }));
    } catch {
      SwalWithTheme.error("CEP", "Não foi possível localizar este CEP");
    }
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleDocumentoChange = (e) => {
    const masked = formatCpfCnpj(e.target.value);
    setForm((prev) => ({ ...prev, documento: masked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await salvarParceiro({
        ...form,
        status: form.status || "ativo",
        usuarioId: form.usuarioId ? Number(form.usuarioId) : null,
      });
      setShowModal(false);
      setForm({
        nome: "",
        documento: "",
        cep: "",
        endereco: "",
        bairro: "",
        cidade: "",
        uf: "",
        nu_endereco: "",
        usuarioId: "",
        status: "ativo",
      });
      setResponsavelQuery("");
      setResponsavelManualInput(false);
      await carregarParceiros();
      SwalWithTheme.success("Sucesso", "Parceiro salvo com sucesso");
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao salvar parceiro";
      SwalWithTheme.error("Erro", message);
    }
  };

  const handleClearFilters = () => {
    setFilterUFs([]);
    setFilterCidades([]);
    setFilterResponsavelIds([]);
    setFilterStatuses([]);
    setProductionPeriodPreset("LAST_31_DAYS");
    const defaultPeriod = resolvePeriodPreset("LAST_31_DAYS");
    setProductionStartDate(defaultPeriod.dataInicio);
    setProductionEndDate(defaultPeriod.dataFim);
    setFiltersOpen(false);
    setPage(0);
  };

  const getPartnerIdKey = (parceiro) => {
    const id = parceiro?.id ?? parceiro?.idParceiro ?? null;
    return id != null ? String(id) : null;
  };

  const handleRowClick = (parceiro) => {
    const idKey = getPartnerIdKey(parceiro);
    if (!idKey) return;
    navigate(`/parceiros/${idKey}`);
  };

  const handleRowSelect = (_, key, isSelected) => {
    if (!key) return;
    setAllMatchingSelected(false);
    setSelectedPartnerIds((prev) => {
      if (isSelected) {
        return [...new Set([...prev, key])];
      }
      return prev.filter((id) => id !== key);
    });
    setSelectedPartnersMap((prev) => {
      const next = { ...prev };
      if (isSelected) {
        const partner = parceirosFiltrados.find((item) => getPartnerIdKey(item) === key);
        if (partner) next[key] = partner;
      } else {
        delete next[key];
      }
      return next;
    });
  };

  const handleSelectAll = (checked) => {
    const currentPageIds = parceirosFiltrados.map((p) => getPartnerIdKey(p)).filter(Boolean);
    if (checked) {
      setSelectedPartnerIds((prev) => Array.from(new Set([...prev, ...currentPageIds])));
      setSelectedPartnersMap((prev) => {
        const next = { ...prev };
        parceirosFiltrados.forEach((partner) => {
          const key = getPartnerIdKey(partner);
          if (key) next[key] = partner;
        });
        return next;
      });
      setAllMatchingSelected(false);
      return;
    }
    if (allMatchingSelected) {
      setSelectedPartnerIds([]);
      setSelectedPartnersMap({});
      setAllMatchingSelected(false);
      return;
    }
    setSelectedPartnerIds((prev) => prev.filter((id) => !currentPageIds.includes(id)));
    setSelectedPartnersMap((prev) => {
      const next = { ...prev };
      currentPageIds.forEach((id) => {
        delete next[id];
      });
      return next;
    });
    setAllMatchingSelected(false);
  };

  const handleSelectAllMatching = async () => {
    setSelectAllMatchingLoading(true);
    try {
      let parceiros = await listarTodosParceirosFiltrados({
        q: searchTerm.trim(),
        ufs: filterUFs,
        cidades: filterCidades,
        responsavelIds: filterResponsavelIds,
        statuses: filterStatuses,
      });
      const nextIds = parceiros.map((partner) => getPartnerIdKey(partner)).filter(Boolean);
      const nextMap = {};
      parceiros.forEach((partner) => {
        const key = getPartnerIdKey(partner);
        if (key) nextMap[key] = partner;
      });
      setSelectedPartnerIds(nextIds);
      setSelectedPartnersMap(nextMap);
      setAllMatchingSelected(true);
    } catch {
      SwalWithTheme.error("Erro", "Não foi possível selecionar todos os parceiros filtrados");
    } finally {
      setSelectAllMatchingLoading(false);
    }
  };

  const allSelected =
    parceirosFiltrados.length > 0 &&
    parceirosFiltrados.every((p) => {
      const key = getPartnerIdKey(p);
      return key && selectedPartnerIds.includes(key);
    });

  const carregarDadosTarefaAux = async () => {
    try {
      const [tipos, motivos] = await Promise.all([
        listarTiposDeTarefa(),
        listarMotivosTarefa(),
      ]);
      setTiposTarefa(tipos);
      setMotivosTarefa(motivos);
    } catch {
      SwalWithTheme.error("Erro", "Falha ao carregar tipos e motivos");
    }
  };

  const handleAbrirTaskModal = async () => {
    await carregarDadosTarefaAux();
    setTaskForm({
      tipoId: "",
      dataHora: "",
      motivoIds: [],
      observacoes: "",
    });
    setShowTaskModal(true);
  };

  const resolveSelectedPartner = (partnerId) =>
    selectedPartnersMap[String(partnerId)] ||
    parceirosFiltrados.find(
      (p) => String(p.id ?? "") === String(partnerId)
    ) ||
    partnerPage.content.find(
      (p) => String(p.id ?? "") === String(partnerId)
    ) ||
    null;

  const buildTaskGroupsByResponsavel = (partnerIds = []) => {
    const groups = new Map();

    partnerIds.forEach((parceiroId) => {
      const partner = resolveSelectedPartner(parceiroId);
      const responsavelId = partner?.usuarioId ?? partner?.usuario?.id ?? null;
      const groupKey = responsavelId != null ? String(responsavelId) : "SEM_RESPONSAVEL";
      const responsavelNome = responsavelId != null
        ? responsavelMap.get(String(responsavelId)) || partner?.usuarioNome || partner?.usuario?.nome || `Responsavel ${responsavelId}`
        : "Sem responsavel vinculado";

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          groupKey,
          responsavelId: responsavelId != null ? Number(responsavelId) : null,
          responsavelNome,
          partnerIds: [],
        });
      }

      groups.get(groupKey).partnerIds.push(Number(parceiroId));
    });

    return Array.from(groups.values());
  };

  const buildTaskGroupsByPartner = (partnerIds = []) => {
    return partnerIds.map((parceiroId) => {
      const partner = resolveSelectedPartner(parceiroId);
      const responsavelId = partner?.usuarioId ?? partner?.usuario?.id ?? null;
      const responsavelNome = responsavelId != null
        ? responsavelMap.get(String(responsavelId)) || partner?.usuarioNome || partner?.usuario?.nome || `Responsavel ${responsavelId}`
        : "Sem responsavel vinculado";

      return {
        groupKey: `PARTNER_${parceiroId}`,
        responsavelId: responsavelId != null ? Number(responsavelId) : null,
        responsavelNome,
        partnerIds: [Number(parceiroId)],
      };
    });
  };

  const handleSalvarTarefasLote = async () => {
    if (savingTaskModal) return;

    if (!taskForm.tipoId || !taskForm.dataHora || (taskForm.motivoIds || []).length === 0) {
      SwalWithTheme.error("Erro", "Preencha tipo, data/hora e ao menos um motivo");
      return;
    }
    if (isTaskScheduleOutsideWindow(taskForm.dataHora, taskScheduleBounds)) {
      SwalWithTheme.error(
        "Erro",
        `A data/hora da tarefa deve estar entre ${TASK_SCHEDULE_PAST_WINDOW_DAYS} dias no passado e ${TASK_SCHEDULE_WINDOW_DAYS} dias no futuro`
      );
      return;
    }
    if (selectedPartnerIds.length === 0) {
      SwalWithTheme.error("Erro", "Selecione ao menos um parceiro");
      return;
    }

    const tipo = tiposTarefa.find((item) => String(item.id) === String(taskForm.tipoId));
    if (!tipo) {
      SwalWithTheme.error("Erro", "Tipo de tarefa invalido");
      return;
    }

    try {
      setSavingTaskModal(true);
      const taskGroups = allowsMultiplePartners(tipo)
        ? buildTaskGroupsByResponsavel(selectedPartnerIds)
        : buildTaskGroupsByPartner(selectedPartnerIds);

      const basePayload = {
        descricaoTarefa: buildTaskTitleFromNotes(taskForm.observacoes),
        dataInicio: normalizeDateTime(taskForm.dataHora),
        observacoes: taskForm.observacoes,
        idTipoDeTarefa: Number(taskForm.tipoId),
        motivosIds: (taskForm.motivoIds || []).map((id) => Number(id)),
      };

      const results = await Promise.allSettled(
        taskGroups.map((group) =>
          criarTarefa({
            ...basePayload,
            parceirosIds: group.partnerIds,
            idUsuario: group.responsavelId ?? null,
          })
        )
      );

      const successCount = results.filter((result) => result.status === "fulfilled").length;
      const failureCount = results.length - successCount;

      if (failureCount > 0) {
        const firstError = results.find((result) => result.status === "rejected");
        const message = firstError?.reason?.response?.data?.message || "Erro ao criar atividades";
        if (successCount > 0) {
          SwalWithTheme.warning("Criacao parcial", `${successCount} tarefa(s) criada(s). ${failureCount} falhou(ram). ${message}`);
        } else {
          SwalWithTheme.error("Erro", message);
          return;
        }
      } else {
        SwalWithTheme.success(
          "Sucesso",
          taskGroups.length === 1
            ? "Atividade criada com sucesso"
            : `${taskGroups.length} tarefas criadas com sucesso`
        );
      }

      setShowTaskModal(false);
      setSelectedPartnerIds([]);
      setSelectedPartnersMap({});
      setAllMatchingSelected(false);
      await carregarParceiros();
    } catch (error) {
      const message = error?.response?.data?.message || "Erro ao criar atividades";
      SwalWithTheme.error("Erro", message);
    } finally {
      setSavingTaskModal(false);
    }
  };

  const getPartnerStatusLabel = (row) => {
    const status = String(row?.status || "").trim().toLowerCase();
    if (!status && row?.ativo === false) return "inativo";
    return status || "ativo";
  };

  const getPartnerStatusVariant = (status) => {
    const normalized = String(status || "").toLowerCase();
    if (normalized === "lead") return "info";
    if (normalized === "inativo") return "danger";
    if (normalized === "ativo") return "success";
    return "default";
  };

  const renderProductionStatus = (row) => {
    const baseClass = "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-transparent px-2.5 py-1 text-xs font-semibold";

    if (row?.producaoStatus === "LOADING") {
      return (
        <span className={`${baseClass} bg-badge-neutral text-badge-neutral-foreground`}>
          <LoaderCircle className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          Verificando
        </span>
      );
    }
    if (row?.producaoStatus === "WITH") {
      return (
        <span
          className={`${baseClass} bg-badge-success text-badge-foreground`}
          title={`Produção bruta no período: ${CURRENCY_FORMATTER.format(Number(row.producaoPeriodo || 0))}`}
        >
          <CircleCheck className="h-3.5 w-3.5" aria-hidden="true" />
          Com produção
        </span>
      );
    }
    if (row?.producaoStatus === "WITHOUT") {
      return (
        <span className={`${baseClass} bg-badge-neutral text-badge-neutral-foreground`}>
          <CircleMinus className="h-3.5 w-3.5" aria-hidden="true" />
          Sem produção
        </span>
      );
    }

    return (
      <span
        className={`${baseClass} bg-badge-warning text-badge-foreground`}
        title={Number(row?.idJoin) > 0 ? "A consulta de produção não foi concluída" : "Parceiro sem vínculo com a JOIN"}
      >
        <TriangleAlert className="h-3.5 w-3.5" aria-hidden="true" />
        Não verificado
      </span>
    );
  };

  const getResponsavelDetails = (row) => {
    const lookupKey = String(row?.usuarioId ?? row?.usuario?.id ?? "");
    const lookupUser = responsavelLookup.get(lookupKey);
    const fallbackName = row?.usuarioNome || row?.usuario?.nome || "";
    const fallbackEmail = row?.usuario?.email || "";
    const fallbackPhoto = row?.usuarioFotoPerfilUrl || row?.usuario?.fotoPerfilUrl || "";

    if (!lookupUser && !fallbackName && !lookupKey) {
      return null;
    }

    return {
      id: row?.usuarioId ?? row?.usuario?.id ?? lookupUser?.id ?? null,
      nome: lookupUser?.nome || fallbackName || `ID ${row?.usuarioId || "-"}`,
      email: lookupUser?.email || fallbackEmail || "",
      fotoPerfilUrl: lookupUser?.fotoPerfilUrl || fallbackPhoto || "",
    };
  };

  const renderResponsavelCell = (row) => {
    const responsavel = getResponsavelDetails(row);

    if (!responsavel) {
      return <span className="text-sm text-muted-foreground">Sem responsavel</span>;
    }

    return (
      <div className="flex min-w-0 items-center gap-3">
        <Avatar className="h-9 w-9 rounded-xl">
          <AvatarImage src={responsavel.fotoPerfilUrl} alt={responsavel.nome || "Responsavel"} />
          <AvatarFallback>{getAvatarInitials(responsavel.nome || "Responsavel")}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{responsavel.nome}</p>
          {responsavel.email ? (
            <p className="truncate text-xs text-muted-foreground">{responsavel.email}</p>
          ) : null}
        </div>
      </div>
    );
  };

  const renderResponsavelCompacto = (row) => {
    const responsavel = getResponsavelDetails(row);
    if (!responsavel) {
      return <span className="text-xs text-muted-foreground">Sem responsável</span>;
    }
    return (
      <span className="flex min-w-0 items-center gap-2">
        <Avatar className="h-6 w-6 rounded-lg">
          <AvatarImage src={responsavel.fotoPerfilUrl} alt={responsavel.nome || "Responsável"} />
          <AvatarFallback className="text-[10px]">{getAvatarInitials(responsavel.nome || "Responsável")}</AvatarFallback>
        </Avatar>
        <span className="truncate text-sm text-muted-foreground">{responsavel.nome}</span>
      </span>
    );
  };

  const columns = [
    {
      key: "nome",
      header: "Parceiro",
      mobilePrimary: true,
      mobileOrder: 0,
      mobileSlot: "title",
      // O card inteiro já abre o parceiro; no celular o nome é só texto.
      mobileRender: (_, row) => row.nome,
      render: (_, row) => (
        <button
          type="button"
          onClick={() => handleRowClick(row)}
          className="text-left text-sm font-semibold text-foreground transition hover:underline"
        >
          {row.nome}
        </button>
      ),
    },
    {
      key: "usuarioId",
      header: "Responsavel",
      mobileSpan: 2,
      mobileOrder: 2,
      mobileSlot: "footer",
      mobileRender: (_, row) => {
        if (usuariosLoading && String(usuarioLogado?.cargo || "").toLowerCase() === "admin") {
          return <Skeleton className="h-4 w-28" />;
        }
        return renderResponsavelCompacto(row);
      },
      render: (_, row) => {
        if (usuariosLoading && String(usuarioLogado?.cargo || "").toLowerCase() === "admin") {
          return <Skeleton className="h-4 w-28" />;
        }
        return renderResponsavelCell(row);
      },
    },
    {
      key: "status",
      header: "Status",
      mobileSpan: 1,
      mobileOrder: 1,
      mobileSlot: "badge",
      render: (_, row) => {
        const statusLabel = getPartnerStatusLabel(row);
        return <StatusBadge status={statusLabel} variant={getPartnerStatusVariant(statusLabel)} />;
      },
    },
    {
      key: "producaoPeriodo",
      header: "Produção no período",
      mobileSpan: 2,
      mobileOrder: 3,
      mobileSlot: "badge",
      render: (_, row) => renderProductionStatus(row),
    },
    {
      key: "cidade",
      header: "Cidade",
      mobileSpan: 1,
      mobileOrder: 1,
      mobileSlot: "subtitle",
      mobileRender: (_, row) => [row.cidade, row.uf].filter(Boolean).join("/") || null,
    },
    { key: "uf", header: "UF", mobileSpan: 1, mobileOrder: 5, mobileSlot: "hidden" },
  ];

  const parceiroModalProgress = calcProgress([
    Boolean(String(form.nome || "").trim()),
    Boolean(String(form.documento || "").trim()),
    Boolean(String(form.cep || "").trim()),
    Boolean(String(form.endereco || "").trim()),
    Boolean(String(form.bairro || "").trim()),
    Boolean(String(form.cidade || "").trim()),
    Boolean(String(form.uf || "").trim()),
    Boolean(String(form.nu_endereco || "").trim()),
    Boolean(String(form.status || "").trim()),
    usuarioLogado?.cargo?.toLowerCase() === "admin" ? Boolean(String(form.usuarioId || "").trim()) : true,
  ]);

  const taskModalProgress = calcProgress([
    Boolean(String(taskForm.tipoId || "").trim()),
    Boolean(String(taskForm.dataHora || "").trim()),
    Array.isArray(taskForm.motivoIds) && taskForm.motivoIds.length > 0,
    String(taskForm.observacoes || "").trim().length >= 15,
    selectedPartnerIds.length > 0,
  ]);

  return (
    <div className="space-y-4">

      <section className="rounded-xl border border-border bg-card p-3 shadow-sm sm:p-4">
        {/* Ações e busca na mesma linha; empilham só em tela estreita. */}
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center">
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button onClick={() => setShowModal(true)}>
              <Plus className="h-4 w-4" />
              Novo parceiro
            </Button>
            <Button
              variant="secondary"
              onClick={handleAbrirTaskModal}
              disabled={selectedPartnerIds.length === 0}
            >
              <WandSparkles className="h-4 w-4" />
              Criar tarefas
            </Button>
          </div>
          <div className="w-full md:max-w-[520px]">
            <SearchBar
              placeholder="Pesquisar parceiro..."
              value={searchTerm}
              onChange={(e) => {
                setPage(0);
                setSearchTerm(e.target.value);
              }}
              className="w-full"
            />
          </div>
          <FiltersSheet
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
            description="Aplicados à lista de parceiros."
            onClear={handleClearFilters}
            clearDisabled={activeFilterCount === 0}
          >
                  <div className="space-y-3">
                    <MultiSelectField
                      label="UF"
                      options={ufOptions.map((uf) => ({ value: uf, label: uf }))}
                      selectedValues={filterUFs}
                      onChange={(values) => {
                        setPage(0);
                        setFilterUFs(values.map(String));
                      }}
                      placeholder="Selecione UFs"
                      searchPlaceholder="Buscar UF..."
                      emptyText="Sem UF disponivel"
                    />
                    <MultiSelectField
                      label="Cidades"
                      options={cidadeOptions.map((cidade) => ({ value: cidade, label: cidade }))}
                      selectedValues={filterCidades}
                      onChange={(values) => {
                        setPage(0);
                        setFilterCidades(values.map(String));
                      }}
                      placeholder="Selecione cidades"
                      searchPlaceholder="Buscar cidade..."
                      emptyText="Sem cidade disponivel"
                    />
                    <MultiSelectField
                      label="Responsaveis"
                      options={responsavelOptions.map((user) => ({
                        value: String(user.id),
                        label: user.nome || `ID ${user.id}`,
                        description: user.email || "",
                      }))}
                      selectedValues={filterResponsavelIds}
                      onChange={(values) => {
                        setPage(0);
                        setFilterResponsavelIds(values.map(String));
                      }}
                      placeholder="Selecione responsaveis"
                      searchPlaceholder="Buscar responsavel..."
                      emptyText="Nenhum responsavel disponivel"
                    />
                    <MultiSelectField
                      label="Status"
                      options={statusOptions}
                      selectedValues={filterStatuses}
                      onChange={(values) => {
                        setPage(0);
                        setFilterStatuses(values.map((value) => String(value).toLowerCase()));
                      }}
                      placeholder="Selecione status"
                      searchPlaceholder="Buscar status..."
                      emptyText="Nenhum status disponivel"
                    />
                    <div className="space-y-3 rounded-xl border border-border bg-muted/20 p-3">
                      <label className="block space-y-1 text-sm font-medium text-foreground">
                        <span>Período da coluna Produção</span>
                        <select
                          className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm"
                          value={productionPeriodPreset}
                          onChange={(event) => {
                            const preset = event.target.value;
                            setPage(0);
                            setProductionPeriodPreset(preset);
                            if (preset !== "CUSTOM") {
                              const period = resolvePeriodPreset(preset);
                              setProductionStartDate(period.dataInicio);
                              setProductionEndDate(period.dataFim);
                            }
                          }}
                        >
                          {PERIOD_PRESETS.map((preset) => (
                            <option key={preset.value} value={preset.value}>{preset.label}</option>
                          ))}
                        </select>
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="space-y-1 text-xs font-medium text-muted-foreground">
                          <span>De</span>
                          <input
                            className="h-10 w-full rounded-md border border-border bg-background px-2 text-sm text-foreground"
                            type="date"
                            value={productionStartDate}
                            max={productionEndDate}
                            onChange={(event) => {
                              setPage(0);
                              setProductionPeriodPreset("CUSTOM");
                              setProductionStartDate(event.target.value);
                            }}
                          />
                        </label>
                        <label className="space-y-1 text-xs font-medium text-muted-foreground">
                          <span>Até</span>
                          <input
                            className="h-10 w-full rounded-md border border-border bg-background px-2 text-sm text-foreground"
                            type="date"
                            value={productionEndDate}
                            min={productionStartDate}
                            onChange={(event) => {
                              setPage(0);
                              setProductionPeriodPreset("CUSTOM");
                              setProductionEndDate(event.target.value);
                            }}
                          />
                        </label>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        A coluna é exibida automaticamente. “Últimos 31 dias” inclui hoje e os 30 dias anteriores.
                      </p>
                    </div>
                  </div>
          </FiltersSheet>
        </div>
        <DataTable
          columns={columns}
          data={parceirosFiltrados}
          onRowClick={handleRowClick}
          selectable
          selectedRows={selectedPartnerIds}
          onRowSelect={handleRowSelect}
          onSelectAll={handleSelectAll}
          onSelectAllMatching={handleSelectAllMatching}
          allSelected={allSelected}
          allMatchingSelected={allMatchingSelected}
          selectAllMatchingLoading={selectAllMatchingLoading}
          rowKey={getPartnerIdKey}
          loading={loading}
          pagination={partnerPage}
          onPageChange={(target) => setPage(target)}
          onPageSizeChange={(size) => {
            setPage(0);
            setPageSize(size);
            setPartnerPage((prev) => ({ ...prev, size }));
          }}
          enablePagination
        />
      </section>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Adicionar parceiro"
        onSave={handleSubmit}
        onCancel={() => setShowModal(false)}
        maxWidth="max-w-2xl"
        progress={parceiroModalProgress}
        progressLabel="Preenchimento do parceiro"
      >
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <FormField label="Nome" name="nome" value={form.nome} onChange={handleChange} required />
            <FormField
              label={getCpfCnpjLabel(form.documento)}
              name="documento"
              value={form.documento}
              onChange={handleDocumentoChange}
              required
            />
            <FormField
              label="Status"
              type="select"
              name="status"
              value={form.status}
              onChange={handleChange}
              required
              options={[
                { value: "ativo", label: "Ativo" },
                { value: "lead", label: "Lead" },
                { value: "inativo", label: "Inativo" },
              ]}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            <FormField
              label="CEP"
              name="cep"
              value={form.cep}
              onChange={handleChange}
              onBlur={handleCepBlur}
              required
            />
            <FormField label="Bairro" name="bairro" value={form.bairro} disabled />
            <FormField label="Cidade" name="cidade" value={form.cidade} disabled />
            <FormField label="UF" name="uf" value={form.uf} disabled />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            <FormField
              label="Endereco"
              name="endereco"
              value={form.endereco}
              disabled
              className="sm:col-span-3"
            />
            <FormField
              label="Numero"
              name="nu_endereco"
              value={form.nu_endereco}
              onChange={handleChange}
              required
            />
          </div>
          {usuarioLogado?.cargo?.toLowerCase() === "admin" && (
            <div className="relative z-10">
              <label className="text-sm font-medium text-foreground">Responsavel</label>
              <div className="relative mt-1">
                <input
                  ref={responsavelInputRef}
                  type="text"
                  placeholder="Pesquise pelo nome"
                  value={responsavelQuery}
                  onFocus={() => setResponsavelListOpen(true)}
                  onChange={(e) => {
                    setResponsavelManualInput(true);
                    setResponsavelQuery(e.target.value);
                    setForm((prev) => ({ ...prev, usuarioId: "" }));
                    setResponsavelListOpen(true);
                  }}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  autoComplete="off"
                />
                {responsavelQuery && (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                    onClick={() => {
                      setForm((prev) => ({ ...prev, usuarioId: "" }));
                      setResponsavelQuery("");
                      setResponsavelManualInput(false);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {responsavelListOpen && (
                <div
                  ref={responsavelDropdownRef}
                  className="absolute left-0 right-0 z-20 mt-1 max-h-52 overflow-y-auto rounded-xl border border-border bg-card p-2 shadow-lg"
                >
                  {responsavelSuggestions.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Nenhum responsavel encontrado</p>
                  ) : (
                    responsavelSuggestions.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        className="flex w-full items-center justify-between rounded-lg px-2 py-1 text-sm hover:bg-accent/40"
                        onClick={() => {
                          setForm((prev) => ({ ...prev, usuarioId: String(user.id) }));
                          setResponsavelQuery(user.nome);
                          setResponsavelManualInput(false);
                          setResponsavelListOpen(false);
                        }}
                      >
                        <span>{user.nome}</span>
                        {user.email && <span className="text-xs text-muted-foreground">{user.email}</span>}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </form>
      </Modal>

      <Modal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        title="Criar atividades para parceiros selecionados"
        onSave={handleSalvarTarefasLote}
        onCancel={() => setShowTaskModal(false)}
        saveDisabled={savingTaskModal}
        saveText={savingTaskModal ? "Salvando..." : "Criar tarefa(s)"}
        maxWidth="max-w-2xl"
        progress={taskModalProgress}
        progressLabel="Preenchimento da atividade"
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField
              label="Tipo de atividade"
              type="select"
              name="tipoId"
              value={taskForm.tipoId}
              onChange={(e) => setTaskForm((prev) => ({ ...prev, tipoId: e.target.value }))}
              options={tiposTarefa.map((t) => ({ value: t.id, label: t.nomeTipo }))}
              required
            />
            <DatePickerField
              label="Data e hora"
              type="datetime-local"
              name="dataHora"
              value={taskForm.dataHora}
              onChange={(e) => setTaskForm((prev) => ({ ...prev, dataHora: e.target.value }))}
              min={taskScheduleBounds.min}
              max={taskScheduleBounds.max}
              required
            />
          </div>

          <div className="rounded-md border border-border bg-muted/25 p-2 text-xs text-muted-foreground">
            A atividade pode ser lancada ate {TASK_SCHEDULE_PAST_WINDOW_DAYS} dias no passado e no maximo para os proximos {TASK_SCHEDULE_WINDOW_DAYS} dias.
          </div>
          <MultiSelectField
            label="Motivos"
            options={motivosTarefa.map((motivo) => ({ value: String(motivo.id), label: motivo.nome }))}
            selectedValues={taskForm.motivoIds}
            onChange={(values) => setTaskForm((prev) => ({ ...prev, motivoIds: values.map(String) }))}
            placeholder="Selecione motivo(s)"
            searchPlaceholder="Buscar motivo..."
            emptyText="Nenhum motivo disponivel"
          />
          <FormField
            label="Observacoes"
            type="textarea"
            name="observacoes"
            value={taskForm.observacoes}
            onChange={(e) => setTaskForm((prev) => ({ ...prev, observacoes: e.target.value }))}
          />
          <div className="rounded-lg border border-border bg-muted/35 p-3 text-sm">
            <p className="mb-2 font-semibold">Parceiros selecionados ({selectedPartnerIds.length})</p>
            {selectedPartnerIds.length > 1 && allowsMultiplePartners(tiposTarefa.find((t) => String(t.id) === String(taskForm.tipoId))) ? (
              <p className="mb-2 text-xs text-muted-foreground">
                Ao salvar, os parceiros serao agrupados automaticamente por responsavel comercial.
              </p>
            ) : null}
            {selectedPartnerIds.length === 0 ? (
              <p className="text-muted-foreground">Nenhum parceiro selecionado.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {selectedPartnerIds.map((pid) => {
                  const partner = resolveSelectedPartner(pid);
                  return <li key={pid}>- {partner?.nome || `Parceiro ${pid}`}</li>;
                })}
              </ul>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
